import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Spectrogram — real-time scrolling waterfall display of microphone
// input frequencies. Uses Web Audio AnalyserNode + Canvas. Color gradient
// chosen for legibility on dark UI: deep purple → violet → orange → yellow.

const COLORMAP = (v) => {
  // v in [0,1]
  // Five-stop gradient
  const stops = [
    [0.00, [0x0A, 0x0B, 0x14]], // background
    [0.10, [0x2A, 0x1A, 0x4D]],
    [0.35, [0x7C, 0x3A, 0xED]],
    [0.55, [0xE0, 0x58, 0x4E]],
    [0.75, [0xF8, 0xB8, 0x4E]],
    [1.00, [0xFF, 0xF6, 0xD0]],
  ];
  for (let i = 1; i < stops.length; i++) {
    if (v <= stops[i][0]) {
      const a = stops[i - 1], b = stops[i];
      const t = (v - a[0]) / (b[0] - a[0]);
      const r = a[1][0] + (b[1][0] - a[1][0]) * t;
      const g = a[1][1] + (b[1][1] - a[1][1]) * t;
      const bl = a[1][2] + (b[1][2] - a[1][2]) * t;
      return [r, g, bl];
    }
  }
  return stops[stops.length - 1][1];
};

export default function Spectrogram() {
  const [state, setState] = useState("idle");
  const [paused, setPaused] = useState(false);
  const [logScale, setLogScale] = useState(false);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const dataRef = useRef(null);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);
  const logRef = useRef(false);

  pausedRef.current = paused;
  logRef.current = logScale;

  const enable = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setState("unsupported"); return; }
    if (!window.isSecureContext) { setState("insecure"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.6;
      src.connect(analyser);
      analyserRef.current = analyser;
      dataRef.current = new Uint8Array(analyser.frequencyBinCount);
      setState("ok");
      requestAnimationFrame(draw);
    } catch {
      setState("denied");
    }
  };

  const draw = () => {
    const cv = canvasRef.current;
    const an = analyserRef.current;
    const data = dataRef.current;
    if (!cv || !an || !data) { rafRef.current = requestAnimationFrame(draw); return; }
    const dpr = window.devicePixelRatio || 1;
    const w = Math.floor(cv.clientWidth * dpr);
    const h = Math.floor(cv.clientHeight * dpr);
    if (cv.width !== w) cv.width = w;
    if (cv.height !== h) cv.height = h;
    const ctx = cv.getContext("2d");

    if (pausedRef.current) { rafRef.current = requestAnimationFrame(draw); return; }

    an.getByteFrequencyData(data);

    // Shift existing image one pixel to the left
    const img = ctx.getImageData(1, 0, w - 1, h);
    ctx.putImageData(img, 0, 0);
    // Draw new column on the right edge
    const colImg = ctx.createImageData(1, h);
    const buf = colImg.data;
    const bins = data.length;
    const useLog = logRef.current;
    const maxFreqIdx = bins; // up to nyquist
    // For each pixel row (y), determine which bin to read from
    for (let y = 0; y < h; y++) {
      let frac;
      if (useLog) {
        // Log scale: top is 20kHz, bottom is 20Hz; row y → fraction
        const minLog = Math.log10(20);
        const maxLog = Math.log10(20000);
        const logHz = maxLog - ((y / (h - 1)) * (maxLog - minLog));
        const hz = Math.pow(10, logHz);
        const nyquist = ctxRef.current.sampleRate / 2;
        frac = hz / nyquist;
      } else {
        frac = 1 - y / (h - 1);
      }
      const bin = Math.max(0, Math.min(bins - 1, Math.floor(frac * maxFreqIdx)));
      const v = data[bin] / 255;
      const [r, g, b] = COLORMAP(v);
      const idx = y * 4;
      buf[idx] = r; buf[idx + 1] = g; buf[idx + 2] = b; buf[idx + 3] = 255;
    }
    ctx.putImageData(colImg, w - 1, 0);

    rafRef.current = requestAnimationFrame(draw);
  };

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (ctxRef.current && ctxRef.current.state !== "closed") ctxRef.current.close();
  }, []);

  return (
    <ToolsShell title="Spectrogram">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Real-time frequency visualization of microphone input. Beautiful for speech, music, environmental sounds. Audio analyzed locally.
          </p>
          <button onClick={enable} style={primaryBtn}>Enable microphone</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Microphone" />
        </div>
      )}

      {state === "ok" && (
        <>
          <div style={{ position: "relative", width: "100%", height: 380, background: "#0A0B14", borderRadius: 12, border: "1px solid #262839", overflow: "hidden" }}>
            <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
            <div style={{ position: "absolute", top: 8, left: 10, background: "rgba(10, 11, 20, 0.85)", padding: "4px 8px", borderRadius: 6, fontSize: 11, color: "#9A9DBE" }}>
              ↑ high · ↓ low Hz
            </div>
            {paused && (
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: "rgba(0,0,0,0.75)", color: "#fff", padding: "6px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                PAUSED
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={() => setPaused((p) => !p)} style={{ background: paused ? TOOLS_ACCENT : "#1C1E2E", color: paused ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {paused ? "Resume" : "Pause"}
            </button>
            <button onClick={() => setLogScale((l) => !l)} style={{ background: logScale ? TOOLS_ACCENT : "#1C1E2E", color: logScale ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {logScale ? "Log Hz" : "Linear Hz"}
            </button>
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            X-axis is time (scrolls right to left). Y-axis is frequency. Brightness = amplitude. Try whistling, speaking, or playing music.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
